import { z } from "zod";
import { type Hex, normalizeHex } from "@/color/oklch";
import { isPaletteKey, PALETTE_KEYS, type PaletteKey } from "./palette";
import {
  isTokenId,
  type Ref,
  SYNTAX_KEYS,
  type SyntaxKey,
  type TokenId,
  type Variant,
} from "./tokens";

export const SCHEMA_URL = "https://themes.utimura.io/schema/v1.json";
export const SCHEMA_VERSION = 1;

export interface SyntaxStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
}

export interface Theme {
  $schema: typeof SCHEMA_URL;
  version: typeof SCHEMA_VERSION;
  id: string;
  name: string;
  variant: Variant;
  palette: Record<PaletteKey, Hex>;
  /** Sparse: only tokens the user changed. Everything else is derived from the palette. */
  overrides: Partial<Record<TokenId, Ref>>;
  /** Sparse syntax style overrides (bold/italic/underline). */
  styles: Partial<Record<SyntaxKey, SyntaxStyle>>;
  /** Palette swatches that generators and adjusters leave alone. */
  locks: PaletteKey[];
  meta: {
    createdAt: string;
    updatedAt: string;
    notes?: string;
    basedOn?: string;
  };
}

export const DEFAULT_STYLES: Partial<Record<SyntaxKey, SyntaxStyle>> = {
  comment: { italic: true },
  link: { underline: true },
  heading: { bold: true },
};

const hexSchema = z.string().transform((v, ctx) => {
  const hex = normalizeHex(v);
  if (!hex) {
    ctx.addIssue({ code: "custom", message: `not a #rrggbb color: ${v}` });
    return z.NEVER;
  }
  return hex;
});

const refSchema = z
  .string()
  .refine((v): v is Ref => isRef(v), "not a $palette, @token or #hex ref");

const styleSchema = z.object({
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  underline: z.boolean().optional(),
});

export const themeSchema = z.object({
  $schema: z.literal(SCHEMA_URL).default(SCHEMA_URL),
  version: z.literal(SCHEMA_VERSION).default(SCHEMA_VERSION),
  id: z
    .string()
    .min(1)
    .max(64)
    .default(() => newId()),
  name: z.string().min(1).max(80),
  variant: z.enum(["dark", "light"]),
  palette: z.object(
    Object.fromEntries(PALETTE_KEYS.map((k) => [k, hexSchema])) as Record<
      PaletteKey,
      typeof hexSchema
    >,
  ),
  overrides: z
    .record(z.string(), refSchema)
    .default({})
    .transform((o) => Object.fromEntries(Object.entries(o).filter(([k]) => isTokenId(k)))),
  styles: z
    .record(z.string(), styleSchema)
    .default({})
    .transform((o) =>
      Object.fromEntries(
        Object.entries(o).filter(([k]) => (SYNTAX_KEYS as readonly string[]).includes(k)),
      ),
    ),
  locks: z
    .array(z.string())
    .default([])
    .transform((l) => l.filter(isPaletteKey)),
  meta: z
    .object({
      createdAt: z.string().default(() => new Date().toISOString()),
      updatedAt: z.string().default(() => new Date().toISOString()),
      notes: z.string().max(4000).optional(),
      basedOn: z.string().max(80).optional(),
    })
    .default(() => ({ createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })),
});

/** Validates untrusted input (files, share links) into a Theme. Throws a readable error. */
export function parseTheme(input: unknown): Theme {
  const result = themeSchema.safeParse(input);
  if (!result.success) {
    const first = result.error.issues[0];
    const where = first?.path.join(".") || "theme";
    throw new Error(`Invalid theme at ${where}: ${first?.message ?? "unknown error"}`);
  }
  return result.data as Theme;
}

export function isRef(value: string): value is Ref {
  if (value.startsWith("$")) return isPaletteKey(value.slice(1));
  if (value.startsWith("@")) return isTokenId(value.slice(1));
  return /^#[0-9a-f]{6}$/.test(value);
}

export function newId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `t-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
}

export function slugify(name: string): string {
  return (
    name
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "theme"
  );
}

export function touch(theme: Theme): Theme {
  return { ...theme, meta: { ...theme.meta, updatedAt: new Date().toISOString() } };
}

/** A copy with a fresh id and timestamps. */
export function cloneTheme(theme: Theme, name = theme.name): Theme {
  const now = new Date().toISOString();
  return structuredClone({
    ...theme,
    id: newId(),
    name,
    meta: { ...theme.meta, createdAt: now, updatedAt: now },
  });
}
